import { ERROR_MESSAGES } from '../constants';

export interface ApiErrorLike {
    message?: string;
    statusCode?: number;
    status?: number;
}

export function hasStatusCode(error: unknown): error is ApiErrorLike {
    if (error === null || typeof error !== 'object') return false;
    return 'statusCode' in error || 'status' in error;
}

export function getErrorStatusCode(error: unknown): number | undefined {
    if (error instanceof FlightServiceError) {
        return error.statusCode;
    }
    if (hasStatusCode(error)) {
        return error.statusCode ?? error.status;
    }
    return undefined;
}

export function isFlightServiceError(error: unknown): error is FlightServiceError {
    return error instanceof FlightServiceError;
}

export function isNotFoundError(error: unknown): boolean {
    if (error instanceof NotFoundError) return true;
    if (error instanceof Error && error.name === 'NotFoundError') return true;
    const statusCode = getErrorStatusCode(error);
    return statusCode === 404;
}

export function isUnauthorizedError(error: unknown): boolean {
    if (error instanceof UnauthorizedError) return true;
    const statusCode = getErrorStatusCode(error);
    if (statusCode === 401) return true;
    if (error instanceof Error && error.message === 'UNAUTHORIZED') return true;
    return false;
}

export function getErrorMessage(error: unknown, defaultMessage = 'Unknown error'): string {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return defaultMessage;
}

export class FlightServiceError extends Error {
    constructor(
        message: string,
        public readonly code: string,
        public readonly statusCode?: number
    ) {
        super(message);
        this.name = 'FlightServiceError';
        Object.setPrototypeOf(this, FlightServiceError.prototype);
    }
}

export class UnauthorizedError extends FlightServiceError {
    constructor() {
        super('User not authorized', ERROR_MESSAGES.UNAUTHORIZED, 401);
        this.name = 'UnauthorizedError';
        Object.setPrototypeOf(this, UnauthorizedError.prototype);
    }
}

export class ServerError extends FlightServiceError {
    constructor(statusCode: number) {
        super('Server error', ERROR_MESSAGES.SERVER_ERROR, statusCode);
        this.name = 'ServerError';
        Object.setPrototypeOf(this, ServerError.prototype);
    }
}

export class NotFoundError extends FlightServiceError {
    constructor() {
        super('Resource not found', ERROR_MESSAGES.NOT_FOUND || 'NOT_FOUND', 404);
        this.name = 'NotFoundError';
        Object.setPrototypeOf(this, NotFoundError.prototype);
    }
}

export class FetchError extends FlightServiceError {
    constructor(originalError?: Error) {
        super(ERROR_MESSAGES.FETCH_FAILED, 'FETCH_ERROR');
        this.name = 'FetchError';
        if (originalError?.stack) {
            this.stack = originalError.stack;
        }
        Object.setPrototypeOf(this, FetchError.prototype);
    }
}

